/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useContext, useEffect, useState } from 'react';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';

/* global chrome */
// Review configuration
const REVIEW_CONFIG = {
  PROMPT_INTERVAL_DAYS: 30, // Only show every 30 days
  MAX_DISMISSALS: 3 // Show 3 times before we stop showing the prompt
};

const ReviewPrompt = ({ shouldCheckOnMount = true }) => {
  const { apiClient } = useContext(GlobalContext);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get the review URL (our Google Business Profile). Still gated on running
  // inside the extension, since the prompt is Chrome-only and handleReview
  // needs chrome.tabs.
  const getExtensionStoreUrl = () => {
    const isExtension = typeof chrome !== 'undefined' && chrome.runtime;

    if (isExtension && chrome.runtime && chrome.runtime.id) {
      return 'https://g.page/r/CfWB6kiLQhY-EAI/review';
    }
    return ''; // Empty string means we're not on Chrome
  };

  // Check if user should see review prompt
  const shouldShowReviewPromptCheck = async () => {
    // First check if we're on Chrome or no extensionStoreUrl, don't show the prompt
    if (!getExtensionStoreUrl()) return false;

    // If we're on Chrome, check if the user should see the prompt
    try {
      let userDetails = await apiClient.getStats();
      if (!userDetails) return false; // If we don't have user details, don't show the prompt

      const reviewData = userDetails?.review || {
        is_reviewed: false,
        missed: 0,
        last_prompt_date: null
      };

      if (reviewData['is_reviewed']) return false; // If the user has already reviewed, don't show the prompt

      if ((reviewData['missed'] || 0) >= REVIEW_CONFIG.MAX_DISMISSALS)
        return false; // If the user has dismissed too many times, don't show the prompt

      // Check if user qualifies: createdAt > 30 days ago AND isSubscribed AND conversationsHad > 30
      if (
        !userDetails?.createdAt ||
        !userDetails?.isSubscribed ||
        userDetails?.conversationsHad < 30
      )
        return false;

      // If the user has been using the extension for less than 30 days, don't show the prompt
      const userCreatedAt = new Date(userDetails.createdAt).getTime();
      const daysSinceCreation =
        (Date.now() - userCreatedAt) / (1000 * 60 * 60 * 24);
      if (daysSinceCreation < 30) return false;

      // Staggered rollout: For first-time prompts, check user creation date
      const lastPromptDate = reviewData['last_prompt_date'] || 0;
      if (lastPromptDate === 0) {
        return (
          new Date(userDetails.createdAt).getDate() === new Date().getDate()
        );
      }
      // For subsequent prompts, use normal 30-day interval
      return (
        (Date.now() - lastPromptDate) / (1000 * 60 * 60 * 24) >=
        REVIEW_CONFIG.PROMPT_INTERVAL_DAYS
      );
    } catch (error) {
      console.error('Error checking if should show review prompt:', error);
      return false;
    }
  };

  // Initialize and check for review prompt
  const initializeReviewPrompt = async () => {
    // Check if we should show prompt
    const shouldShow = await shouldShowReviewPromptCheck();
    if (shouldShow) {
      setShowReviewPrompt(true);
    }
  };

  // Listen for custom event to show review prompt (manual trigger)
  useEffect(() => {
    const handleShowReviewPrompt = () => {
      setShowReviewPrompt(true);
    };

    window.addEventListener('show-review-prompt', handleShowReviewPrompt);

    return () => {
      window.removeEventListener('show-review-prompt', handleShowReviewPrompt);
    };
  }, []);

  // Handle "Leave Review" button
  const handleReview = async () => {
    setIsLoading(true);
    const url = getExtensionStoreUrl();
    const isExtension = typeof chrome !== 'undefined' && chrome.runtime;
    if (!isExtension || !url) return; // If we're not on Chrome, don't open the review page
    await chrome.tabs.create({ url: url, active: true });

    // Mark as reviewed
    let userDetails = await apiClient.getStats();
    const currentReview = userDetails.review || {};
    await apiClient.editUserSettings({
      review: {
        is_reviewed: true,
        missed: currentReview['missed'] || 0,
        last_prompt_date: currentReview['last_prompt_date'] || Date.now()
      }
    });
    setShowReviewPrompt(false);
    setIsLoading(false);
  };

  // Handle "No Thanks" button
  const handleDismiss = async () => {
    setIsLoading(true);
    let userDetails = await apiClient.getStats();
    const currentReview = userDetails.review || {};
    await apiClient.editUserSettings({
      review: {
        is_reviewed: currentReview['is_reviewed'] || false,
        missed: (currentReview['missed'] || 0) + 1,
        last_prompt_date: Date.now()
      }
    });
    setShowReviewPrompt(false);
    setIsLoading(false);
  };

  // Initialize review prompt when component mounts
  useEffect(() => {
    if (shouldCheckOnMount && apiClient && apiClient.haveSessionKey()) {
      initializeReviewPrompt();
    }
  }, [shouldCheckOnMount, apiClient]);

  // Render the modal
  return (
    showReviewPrompt && (
      <div className="modal-overlay">
        <div className="modal-content">
          <div
            className="d-flex align-items-center mb-3"
            style={{ justifyContent: 'center' }}
          >
            <img
              alt="Comprehend Logo"
              src="/logo192.png"
              style={{ height: '32px', marginRight: '10px' }}
            />
            <h1 className="mb-0 text-primary">❤️ Comprehend?</h1>
          </div>
          <div className="mb-3">
            <span style={{ fontSize: '1rem' }}>⭐⭐⭐⭐⭐</span>
          </div>
          <h6 className="mb-2" style={{ fontSize: '18px', fontWeight: '500' }}>
            Share how Comprehend has helped you
          </h6>
          <p className="text-muted mb-3" style={{ fontSize: '15px' }}>
            We'd love to hear how it improved your notes or saved you time!
          </p>
          <Row className="w-100" style={{ margin: 0 }}>
            <Col xs="12" className="mb-2">
              <Button
                color="primary"
                size="lg"
                block
                onClick={handleReview}
                disabled={isLoading}
                style={{
                  background: 'linear-gradient(87deg, #11cdef 0, #1171ef 100%)',
                  border: 'none',
                  fontWeight: '600',
                  width: '100%'
                }}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Loading...
                  </>
                ) : (
                  '⭐ Leave Review'
                )}
              </Button>
            </Col>
            <Col xs="12" className="d-flex justify-content-center">
              <Button
                color="light"
                size="sm"
                outline
                onClick={handleDismiss}
                disabled={isLoading}
                style={{ minWidth: '120px' }}
              >
                Maybe Later
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    )
  );
};

export default ReviewPrompt;
